import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAllowedEmailDomains } from '@/hooks/useAllowedEmailDomains';
import { useAuth } from '@/contexts/AuthContext';
import { X, Loader2, ShieldCheck } from 'lucide-react';

export function AllowedDomainsCard() {
  const { user } = useAuth();
  const { domains, isLoading, addDomain, removeDomain, isAdding } = useAllowedEmailDomains();
  const [input, setInput] = useState('');

  if (user?.role !== 'super_admin') return null;

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    addDomain(input);
    setInput('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          Allowed Email Domains
        </CardTitle>
        <CardDescription>
          Restrict which email addresses can be added to this organization. When the list is empty,
          any email is allowed. Existing users are not affected.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={handleAdd} className="flex gap-2">
          <Input
            placeholder="company.com"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isAdding}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <Button type="submit" disabled={isAdding || !input.trim()}>
            {isAdding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add
          </Button>
        </form>

        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : domains.length === 0 ? (
          <div className="rounded-lg border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground">
            No domains configured — any email can be added. Add at least one to restrict sign-ups
            to your organization.
          </div>
        ) : (
          <ul className="space-y-2">
            {domains.map((d) => (
              <li
                key={d.id}
                className="flex items-center justify-between rounded-md border bg-card px-3 py-2"
              >
                <span className="font-mono text-sm">@{d.domain}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeDomain(d.id)}
                  aria-label={`Remove ${d.domain}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
